import { useState } from 'react';
import CpmEmptyState from '../../../components/design-system/EmptyState';
import CpmErrorCard from '../../../components/design-system/ErrorCard';
import CpmSkeletonRow from '../../../components/design-system/SkeletonRow';
import CpmTooltip from '../../../components/design-system/Tooltip';
import CpmButton from '../../../components/design-system/Button';
import type { StarterSummary } from '../../../types/accessManagement.types';
import { STARTER_COPY } from '../starterCopy';

interface StarterTableProps {
    rows: StarterSummary[];
    stateNames: Record<string, string>;
    listState: 'loading' | 'ready' | 'error';
    hasStarters: boolean;
    onlySearch: boolean;
    searchLabel: string;
    sortDir: 'asc' | 'desc' | null;
    onSort: () => void;
    onOpen: (id: string) => void;
    onRetry: () => void;
    onClear: () => void;
}

function signInText(starter: StarterSummary): string {
    if (starter.signInDashboard && starter.signInPos) return STARTER_COPY.SIGN_BOTH;
    if (starter.signInDashboard) return STARTER_COPY.DASHBOARD;
    if (starter.signInPos) return STARTER_COPY.POS;
    return STARTER_COPY.NO_SIGN_IN;
}

function stickyHead(width: string, padding: string) {
    return {
        position: 'sticky' as const,
        top: 64,
        zIndex: 4,
        width,
        padding,
        background: '#EDEDF3',
        borderBottom: '1.5px solid #D7D7E0',
        textAlign: 'left' as const,
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '0.08em',
        textTransform: 'uppercase' as const,
        color: '#1A1A1A',
    };
}

function SortMarks({ dir, active }: { dir: 'asc' | 'desc'; active: boolean }) {
    const up = active && dir === 'asc' ? '#6E64F0' : 'var(--cpm-text-secondary)';
    const down = active && dir === 'desc' ? '#6E64F0' : 'var(--cpm-text-secondary)';
    return (
        <svg width="9" height="13" viewBox="0 0 8 12" style={{ flex: 'none', maxWidth: 'none' }}>
            <path d="M4 0.6 L7.2 4.6 L0.8 4.6 Z" fill={up} />
            <path d="M4 11.4 L7.2 7.4 L0.8 7.4 Z" fill={down} />
        </svg>
    );
}

function NameHead({ active, dir, onSort }: { active: boolean; dir: 'asc' | 'desc'; onSort: () => void }) {
    const [hover, setHover] = useState(false);
    return (
        <th style={{ ...stickyHead('22%', '0') }}>
            <button
                type="button"
                onClick={onSort}
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                className="inline-flex items-center bg-transparent border-0"
                style={{
                    gap: 6,
                    padding: '12px 20px',
                    fontFamily: 'inherit',
                    fontSize: 10.5,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: hover ? '#6E64F0' : '#0E0858',
                    cursor: 'pointer',
                }}
            >
                {STARTER_COPY.COL_NAME}
                <SortMarks dir={dir} active={active} />
            </button>
        </th>
    );
}

export default function StarterTable({
    rows,
    stateNames,
    listState,
    hasStarters,
    onlySearch,
    searchLabel,
    sortDir,
    onSort,
    onOpen,
    onRetry,
    onClear,
}: StarterTableProps) {
    const ready = listState === 'ready';
    const showRows = ready && rows.length > 0;
    const trueEmpty = ready && !hasStarters;
    const noMatch = ready && hasStarters && rows.length === 0;

    return (
        <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead>
                    <tr>
                        <NameHead active={sortDir !== null} dir={sortDir ?? 'asc'} onSort={onSort} />
                        <th style={stickyHead('38%', '12px 16px')}>{STARTER_COPY.COL_DESCRIPTION}</th>
                        <th style={stickyHead('24%', '12px 16px')}>{STARTER_COPY.COL_STATES}</th>
                        <th style={stickyHead('16%', '12px 20px 12px 16px')}>{STARTER_COPY.COL_SIGN_IN}</th>
                    </tr>
                </thead>
                <tbody>
                    {showRows
                        ? rows.map((starter) => {
                              const named = (starter.stateIds || []).map((id) => ({
                                  id,
                                  name: stateNames[id] || id,
                              }));
                              const every = named.length === 0;
                              const shown = named.slice(0, 2);
                              const more = named.length - shown.length;
                              const names = named.map((state) => state.name);
                              const signedIn = starter.signInDashboard || starter.signInPos;
                              return (
                                  <tr
                                      key={starter._id}
                                      onClick={() => onOpen(starter._id)}
                                      onMouseEnter={(event) => {
                                          event.currentTarget.style.background = '#FAFAFF';
                                      }}
                                      onMouseLeave={(event) => {
                                          event.currentTarget.style.background = '';
                                      }}
                                      style={{
                                          borderBottom: '1px solid var(--cpm-border-default)',
                                          cursor: 'pointer',
                                          transition: 'background 150ms ease',
                                      }}
                                  >
                                      <td
                                          style={{
                                              padding: '15px 16px 15px 20px',
                                              fontSize: 14,
                                              fontWeight: 800,
                                              letterSpacing: '-0.01em',
                                              overflowWrap: 'anywhere',
                                          }}
                                      >
                                          {starter.name}
                                      </td>
                                      <td
                                          style={{
                                              padding: '15px 16px',
                                              fontSize: 13,
                                              fontWeight: 500,
                                              lineHeight: 1.45,
                                              color: 'var(--cpm-text-secondary)',
                                              overflowWrap: 'anywhere',
                                          }}
                                      >
                                          {starter.description}
                                      </td>
                                      <td style={{ padding: '15px 16px' }}>
                                          {every ? (
                                              <span style={{ fontSize: 13, fontWeight: 700 }}>
                                                  {STARTER_COPY.EVERY_STATE}
                                              </span>
                                          ) : (
                                              <div className="flex items-center flex-wrap" style={{ gap: 6 }}>
                                                  {shown.map((state) => (
                                                      <span
                                                          key={state.id}
                                                          style={{
                                                              display: 'inline-flex',
                                                              alignItems: 'center',
                                                              background: '#F1F1F6',
                                                              color: '#1A1A1A',
                                                              padding: '4px 10px',
                                                              fontSize: 12.5,
                                                              fontWeight: 700,
                                                              borderRadius: 7,
                                                              lineHeight: 1,
                                                          }}
                                                      >
                                                          {state.name}
                                                      </span>
                                                  ))}
                                                  {more > 0 ? (
                                                      <CpmTooltip
                                                          tone="light"
                                                          fit
                                                          maxWidth={240}
                                                          content={
                                                              <span>
                                                                  <span
                                                                      style={{
                                                                          display: 'block',
                                                                          marginBottom: 6,
                                                                          fontSize: 9.5,
                                                                          fontWeight: 800,
                                                                          letterSpacing: '0.08em',
                                                                          textTransform: 'uppercase',
                                                                          color: 'var(--cpm-text-secondary)',
                                                                      }}
                                                                  >
                                                                      {names.length} {STARTER_COPY.STATES_WORD}
                                                                  </span>
                                                                  {names.map((name) => (
                                                                      <span
                                                                          key={name}
                                                                          style={{
                                                                              display: 'block',
                                                                              fontSize: 13,
                                                                              fontWeight: 700,
                                                                              whiteSpace: 'nowrap',
                                                                          }}
                                                                      >
                                                                          {name}
                                                                      </span>
                                                                  ))}
                                                              </span>
                                                          }
                                                      >
                                                          <span
                                                              style={{
                                                                  display: 'inline-flex',
                                                                  padding: '4px 9px',
                                                                  borderRadius: 7,
                                                                  background: 'var(--cpm-purple-tint-13)',
                                                                  color: 'var(--cpm-brand-purple)',
                                                                  fontSize: 12.5,
                                                                  fontWeight: 800,
                                                              }}
                                                          >
                                                              +{more}
                                                          </span>
                                                      </CpmTooltip>
                                                  ) : null}
                                              </div>
                                          )}
                                      </td>
                                      <td
                                          style={{
                                              padding: '15px 20px 15px 16px',
                                              fontSize: 13,
                                              fontWeight: 700,
                                              color: signedIn ? 'var(--cpm-text-primary)' : 'var(--cpm-text-secondary)',
                                          }}
                                      >
                                          {signInText(starter)}
                                      </td>
                                  </tr>
                              );
                          })
                        : null}
                </tbody>
            </table>
            {ready && hasStarters ? (
                <div style={{ padding: '0 20px 16px' }}>
                    <CpmButton variant="outline" size="sm" onClick={onRetry}>
                        {STARTER_COPY.RETRY}
                    </CpmButton>
                </div>
            ) : null}
            {listState === 'loading' ? (
                <div>
                    {Array.from({ length: 6 }, (_, index) => (
                        <CpmSkeletonRow key={index} index={index} />
                    ))}
                </div>
            ) : null}
            {listState === 'error' ? (
                <div style={{ padding: 20 }}>
                    <CpmErrorCard
                        title={STARTER_COPY.ERROR_TITLE}
                        body={STARTER_COPY.ERROR_BODY}
                        onRetry={onRetry}
                        retryLabel={STARTER_COPY.RETRY}
                    />
                </div>
            ) : null}
            {trueEmpty ? (
                <CpmEmptyState title={STARTER_COPY.EMPTY_TITLE} description={STARTER_COPY.EMPTY_BODY} />
            ) : null}
            {noMatch ? (
                <CpmEmptyState
                    title={
                        onlySearch
                            ? `${STARTER_COPY.NO_NAME_PREFIX} “${searchLabel}”`
                            : STARTER_COPY.NO_MATCH_TITLE
                    }
                    description={onlySearch ? STARTER_COPY.NO_NAME_BODY : STARTER_COPY.NO_MATCH_BODY}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onClear}>
                            {onlySearch ? STARTER_COPY.CLEAR_SEARCH : STARTER_COPY.CLEAR_ALL}
                        </CpmButton>
                    }
                />
            ) : null}
        </div>
    );
}
